/* eslint-disable prettier/prettier */
import React, { useState, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import styled from "styled-components/macro";

import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";
import Typography from "@material-ui/core/Typography";

import JourneysSelect from "../shared/JourneysSelect";
import { getFieldProps } from "../utils";

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  align-items: center;
  flex-direction: column;
`;

const validationSchema = () =>
  yup.object().shape({
    targetJourneyId: yup.array().of(yup.string()).min(1).required("Please select at least one journey"),
  });

const CopyMaterialModal = ({ open, onClose, onConfirm, journeys, material }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Checking if the material has unsupported type for copying
  const hasUnsupportedType = useMemo(() => {
    if (!material) return false;
    const unsupportedTypes = ["SEMANTIC", "SURVEY"];
    return unsupportedTypes.includes(material.type);
  }, [material]);

  const formik = useFormik({
    initialValues: {
      targetJourneyId: [],
    },
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        console.log('Selected journeys:', values.targetJourneyId);
        await onConfirm(values.targetJourneyId);
        onClose();
      } catch (error) {
        console.error('Error copying materials:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    validationSchema: validationSchema(),
    validator: () => ({}),
  });

  const handleTargetJourneyIdChange = useCallback(
    (value) => {
      formik.setFieldValue("targetJourneyId", value);
    },
    [formik]
  );

  const handleClose = () => {
    if (!isSubmitting) {
      formik.resetForm();
      onClose();
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between">
          Copy Learning Materials
          <IconButton disabled={isSubmitting} size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ overflowY: "visible" }}>
        <Grid container spacing={4}>
          {hasUnsupportedType && (
            <Grid item xs={12}>
              <Alert severity="warning" variant="outlined">
                <Typography variant="body2">
                  Note: This material has a type which is not supported for copying and will not be copied correctly.
                </Typography>
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            {!journeys ? (
              <LoaderContainer>
                <CircularProgress size={30} />
              </LoaderContainer>
            ) : (
              <JourneysSelect
                {...getFieldProps(formik, {
                  name: "targetJourneyId",
                })}
                journeys={journeys}
                label="Target Journeys"
                multiple={true}
                onChange={handleTargetJourneyIdChange}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={
            !formik.dirty ||
            isSubmitting ||
            formik.values.targetJourneyId.length === 0
          }
          endIcon={isSubmitting && <CircularProgress size={25} />}
          variant="contained"
          onClick={formik.handleSubmit}
        >
          Copy
        </Button>
        <Button
          color="primary"
          disabled={isSubmitting}
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyMaterialModal; 